import * as React from "react";

import {
  ThreeDPanelState,
  usePanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { setThreeDPanelConfig } from "@/shared/state/visualization/actions";
import {
  CAMERA_TYPE,
  DEFAULT_ENTITY_ID,
  type Camera,
  type OrbitCamera,
} from "@/shared/state/visualization/schema/v1/three-d";

import { OrbitCameraFields } from "./OrbitCameraFields";
import { ThirdPersonCameraFields } from "./ThirdPersonCameraFields";

interface CameraSettingsProps {
  panelId: string;
}

/**
 * The panel's camera editor: whichever of {@link OrbitCameraFields} and
 * {@link ThirdPersonCameraFields} matches the camera the panel is configured
 * for, wired to the visualization store.
 *
 * `camera` is a discriminated union with no fields in common — orbit tracks an
 * entity within a zoom envelope, third person frames a free-floating point at an
 * angle and a distance — so the two editors are separate components rather than
 * one with optional fields, and neither can write the other's shape.
 *
 * This was orbit-only, which was survivable while a third-person camera was
 * something the user opted into with the header's mode toggle: the controls fell
 * back to a placeholder orbit spec and went read-only. It stopped being
 * survivable when importing a model started producing one, because the panel
 * could then arrive at a camera the user had no way to edit. Imports no longer
 * change the camera's type, but the mode toggle still reaches third person and a
 * saved workspace may already hold one, so both editors stay.
 */
export function CameraSettings({ panelId }: CameraSettingsProps) {
  const dispatch = useVizDispatch();
  const panelState = usePanelState(panelId) as ThreeDPanelState;
  const stored = panelState.config?.camera;

  const onChange = React.useCallback(
    (updatedCamera: Camera) => {
      dispatch(setThreeDPanelConfig(panelId, { camera: updatedCamera }));
    },
    [dispatch, panelId],
  );

  // A panel with no camera of its own gets the orbit default the renderer would
  // build for it, so the controls have something to display.
  const orbit: OrbitCamera = React.useMemo(() => {
    if (stored?.type === CAMERA_TYPE.orbit) {
      return stored;
    }
    return {
      type: CAMERA_TYPE.orbit,
      target: { id: DEFAULT_ENTITY_ID, mesh: "mesh" as const },
      zoom: { min: 1, max: 50 },
    };
  }, [stored]);

  if (stored?.type === CAMERA_TYPE.thirdPerson) {
    return <ThirdPersonCameraFields value={stored} onChange={onChange} />;
  }

  return (
    <OrbitCameraFields
      value={orbit}
      entities={panelState.data.entities}
      // Read-only for a panel carrying no camera at all, as it has always been:
      // the spec above is a placeholder for display, and editing it would write
      // a target and an envelope the panel never declared. See
      // {@link OrbitCameraFields} for why "read-only" also means "unvalidated".
      onChange={stored?.type === CAMERA_TYPE.orbit ? onChange : undefined}
    />
  );
}
